/**
 * Support Tasks tab counts — pure bucketing, shared by the counts query and its tests.
 *
 * Reported 2026-10-03 (QA, support_agent): every tab except the selected one showed 0. The page
 * only had real numbers for admins (the get_support_task_counts RPC is not wired for agents), so a
 * support agent saw "Past Due 0" while Pending held a task 162 days overdue. Counts now come from
 * the SAME scope and the SAME client-side filters the lists use, so a badge always equals what the
 * tab would list.
 */
import type {
  CustomerSnapshot,
  SupportTask,
  TaskTabKey,
} from "../types/supportTasks.types";
import { matchesSupportTaskSearch } from "./supportTaskSearch";

export type SupportTaskTabCounts = Record<TaskTabKey, number>;

/** The cutoff getPastDueTasks uses: due_date < this (local midnight, ISO date). */
export function pastDueCutoffISO(now: Date = new Date()): string {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return today.toISOString().split("T")[0];
}

/** "Due today" exactly as the page's showDueTodayOnly filter computes it. */
function dueTodayISO(now: Date): string {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  return today.toISOString().split("T")[0];
}

export type CountableTask = Pick<
  SupportTask,
  | "id"
  | "status"
  | "due_date"
  | "agent_id"
  | "external_task_id"
  | "customer_snapshot"
  | "tags"
> & { agent?: { name?: string | null } | null };

export interface TabCountFilters {
  searchQuery?: string | null;
  tag?: string | null;
  dueTodayOnly?: boolean;
}

/** The page's client-side filters (search, tag, due today), applied the way the lists apply them. */
export function passesClientFilters(
  task: CountableTask,
  f: TabCountFilters,
  now: Date = new Date(),
): boolean {
  if (f.searchQuery && !matchesSupportTaskSearch(task, f.searchQuery)) {
    return false;
  }
  if (f.tag) {
    const snapshot = task.customer_snapshot as CustomerSnapshot;
    if (!(snapshot?.tags?.includes(f.tag) || task.tags?.includes(f.tag))) {
      return false;
    }
  }
  if (f.dueTodayOnly) {
    if (!task.due_date || task.due_date.split("T")[0] !== dueTodayISO(now)) {
      return false;
    }
  }
  return true;
}

const isOpen = (t: CountableTask) =>
  t.status === "pending" || t.status === "in_progress";

/**
 * Bucket rows into tab counts.
 * @param scoped      rows in the current scope (my tasks / escalated to me / unassigned), any status
 * @param unassigned  open rows with agent_id IS NULL (the Unassigned tab ignores scope, as its list does)
 * @param completedHeadCount  when there are no client-side filters the caller may pass an exact
 *                    server count for Completed instead of fetching every completed row
 */
export function bucketSupportTaskTabCounts(
  scoped: CountableTask[],
  unassigned: CountableTask[],
  f: TabCountFilters,
  completedHeadCount: number | null = null,
  now: Date = new Date(),
): SupportTaskTabCounts {
  const cutoff = pastDueCutoffISO(now);
  const counts: SupportTaskTabCounts = {
    past_due: 0,
    pending: 0,
    in_progress: 0,
    completed: completedHeadCount ?? 0,
    unassigned: 0,
  };
  for (const t of scoped) {
    if (!passesClientFilters(t, f, now)) {
      continue;
    }
    if (t.status === "pending") {
      counts.pending++;
    } else if (t.status === "in_progress") {
      counts.in_progress++;
    } else if (t.status === "completed" && completedHeadCount === null) {
      counts.completed++;
    }
    if (isOpen(t) && t.due_date && t.due_date.split("T")[0] < cutoff) {
      counts.past_due++;
    }
  }
  for (const t of unassigned) {
    if (
      isOpen(t) &&
      (t.agent_id === null || t.agent_id === undefined) &&
      passesClientFilters(t, f, now)
    ) {
      counts.unassigned++;
    }
  }
  return counts;
}
